import axios from 'axios';
import { createAsyncThunk } from 'app/store/utils';
import { Link } from 'types/api.types';
import {
  fetchUnseenNotificationLink,
  getInvitationsListLink,
  getInviteByTokenLink,
  getMarkNotificationAsReadLink,
  getNotificationsListLink,
  getRespondToInviteLink,
  getTranferInvitesLink,
  getUnseenInviteLink,
} from 'app/store/Notifications/selectors';
import { apiErrorHandler, buildQueryUrl } from 'app/utils/utils';
import { addAlertAction } from 'app/store/Alerts/slice';
import {
  FetchNotificationCountResponse,
  FetchNotificationResponse,
} from 'types/notification.types';
import {
  FetchInviteCountResponse,
  FetchInviteResponse,
  FetchInvitesResponse,
} from 'types/invite.types';
import { ApiErrorCodeMessages, ApiErrorCodeTypes } from 'types/enums';
import { RespondToInviteEffectData } from './effects.types';

export const fetchNotificationsEffect = createAsyncThunk(
  'notifications/fetch',
  async (data: { next?: Link; limit?: number } | undefined, { getState }) => {
    const state = getState();
    const fetchNotificationsLink = getNotificationsListLink(state)?.href || '';
    const link =
      data?.next?.href ||
      buildQueryUrl(fetchNotificationsLink, {
        limit: data?.limit ? data.limit.toString() : '',
      });
    if (!link) {
      throw new Error('Fetch Notifications link not found');
    }
    const response = await axios.get<FetchNotificationResponse>(link, {
      withCredentials: true,
    });
    const { items, _links } = response.data.response.inAppNotificationLog;
    return { items, _links };
  }
);

export const fetchUnseenNotificationCountEffect = createAsyncThunk(
  'notifications/fetchUnseenCount',
  async (_, { getState }) => {
    const link = fetchUnseenNotificationLink(getState());
    if (!link) {
      throw new Error('Fetch unseen Notifications link not found');
    }
    const response = await axios.get<FetchNotificationCountResponse>(link, {
      withCredentials: true,
    });

    return {
      newNotificationsCount:
        response.data.response.newNotificationsCount?.item.count,
      newInvitesCount: response.data.response.newNotificationsCount?.item.count,
      totalCount: response.data.response.totalCount?.item.count,
      unreadChatMessagesCount:
        response.data.response.unreadChatMessagesCount?.item.count,
    };
  }
);

export const fetchUnseenInviteCountEffect = createAsyncThunk(
  'notifications/fetchUnseenInvitesCount',
  async (_, { getState }) => {
    const link = getUnseenInviteLink(getState());
    if (!link) {
      throw new Error('Fetch unseen Invites link not found');
    }
    const response = await axios.get<FetchInviteCountResponse>(link, {
      withCredentials: true,
    });
    return response.data.response.newInvitesCount.item.count;
  }
);

export const fetchInvitesEffect = createAsyncThunk(
  'notifications/fetchInvites',
  async (data: { next?: Link; limit?: number } | undefined, { getState }) => {
    const link = getInvitationsListLink(getState());
    if (!link) {
      throw new Error('Fetch invites link not found');
    }
    const response = await axios.get<FetchInvitesResponse>(
      data?.next?.href ||
        buildQueryUrl(link.href, {
          limit: data?.limit ? data.limit.toString() : '',
        }),
      {
        withCredentials: true,
      }
    );
    const { items, _links } = response.data.response.invite;
    return { items, _links };
  }
);

export const respondToInviteEffect = createAsyncThunk(
  'notifications/respondToInvite',
  async (
    data: RespondToInviteEffectData,
    { getState, dispatch, rejectWithValue }
  ) => {
    const { guid, response: inviteResponse } = data;
    const link = getRespondToInviteLink(getState());
    try {
      if (!link) {
        throw new Error('Respond to invite link not found');
      }
      await axios.post(
        link.href.replace('{inviteGuid}', guid),
        { inviteResponse },
        { withCredentials: true }
      );
      dispatch(
        addAlertAction({
          message: `Invite ${
            inviteResponse === 'ACCEPT' ? 'accepted' : 'declined'
          }`,
        })
      );
      return guid;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const markNotificationAsReadEffect = createAsyncThunk(
  'notifications/markNotificationAsRead',
  async (data: { guid: string }, { getState, dispatch }) => {
    const link = getMarkNotificationAsReadLink(getState());
    try {
      if (!link) {
        throw new Error('Mark notification as read link not found');
      }
      await axios.patch(
        link.href.replace('{notificationGuid}', data.guid),
        {
          read: true,
        },
        { withCredentials: true }
      );
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Something went wrong. Please try again' })
      );
    }
  }
);

export const transferInvitesEffect = createAsyncThunk(
  'notifications/transferInvites',
  async (data: { inviteToken: string }, { getState, dispatch }) => {
    const link = getTranferInvitesLink(getState());
    try {
      if (!link) {
        throw new Error('Tranfer Invites link not found');
      }
      await axios.post<FetchInviteResponse>(
        link.href,
        {
          inviteToken: data.inviteToken,
        },
        { withCredentials: true }
      );
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Something went wrong. Please try again' })
      );
      throw e;
    }
  }
);

export const getInviteByTokenEffect = createAsyncThunk(
  'notifications/getInviteByToken',
  async (data: { inviteToken: string }, { getState, dispatch }) => {
    const getInviteByTokenHref = getInviteByTokenLink(getState())?.href;
    try {
      if (!getInviteByTokenHref) {
        throw new Error('Get Invite By Token link not found');
      }
      const response = await axios.get<FetchInviteResponse>(
        getInviteByTokenHref.replace('{inviteToken}', data.inviteToken),
        { withCredentials: true }
      );
      return response.data.response.invite.item;
    } catch (e: any) {
      if (
        [
          ApiErrorCodeTypes.INVITE_CONSUMED,
          ApiErrorCodeTypes.ASSOCIATED_WITH_ANOTHER_USER,
        ].includes(e.response?.data?.error?.errorCode)
      ) {
        dispatch(
          addAlertAction({
            message:
              ApiErrorCodeMessages[
                e.response.data.error.errorCode as ApiErrorCodeTypes
              ],
          })
        );
      } else {
        dispatch(
          addAlertAction({ message: apiErrorHandler(e?.response, 'invite') })
        );
      }
      throw e;
    }
  }
);
